const FULL_HTML_DOCUMENT_PATTERN = /<!doctype\s+html|<html[\s>]/i;
const HTML_DOCUMENT_STRUCTURE_PATTERN = /<!doctype\s+html|<html[\s>]|<head[\s>]|<body[\s>]|<style[\s>]/i;
const VOID_HTML_TAGS_PATTERN = /^<(area|base|br|col|embed|hr|img|input|link|meta|param|source|track|wbr)(\s[^>]*)?>$/i;

export interface HtmlDocumentParts {
    head: string;
    body: string;
}

export const isFullHtmlDocument = (source: string): boolean => FULL_HTML_DOCUMENT_PATTERN.test(source);

export const hasHtmlDocumentStructure = (source: string): boolean => HTML_DOCUMENT_STRUCTURE_PATTERN.test(source);

export const unwrapHtmlElement = (content: string, tag: 'head' | 'body'): string => {
    const match = content.match(new RegExp(`<${tag}[^>]*>([\\s\\S]*)<\\/${tag}>`, 'i'));
    return (match?.[1] ?? content).trim();
};

export const extractHtmlParts = (head: string | null | undefined, body: string): HtmlDocumentParts => {
    if (!isFullHtmlDocument(body)) {
        return {
            head: head ?? '',
            body: unwrapHtmlElement(body, 'body')
        };
    }

    return {
        head: unwrapHtmlElement(body, 'head'),
        body: unwrapHtmlElement(body, 'body')
    };
};

export const formatHtmlSource = (source: string): string => {
    let indent = 0;
    return source
        .replace(/>\s+</g, '>\n<')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
            const isClosingTag = /^<\//.test(line);
            const isOpeningTag = /^<([a-z][\w:-]*)(\s[^>]*)?>$/i.test(line);
            const isSelfClosingTag = /\/>$/.test(line) || VOID_HTML_TAGS_PATTERN.test(line);
            const isDeclaration = /^<!|^<\?/.test(line);

            if (isClosingTag) {
                indent = Math.max(indent - 1, 0);
            }

            const formatted = `${'    '.repeat(indent)}${line}`;

            if (isOpeningTag && !isClosingTag && !isSelfClosingTag && !isDeclaration) {
                indent += 1;
            }

            return formatted;
        })
        .join('\n');
};

const escapeHtml = (source: string): string =>
    source.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');

export const buildHtmlDocument = (head: string | null | undefined, body: string): string => {
    if (isFullHtmlDocument(body)) return body;

    const normalizedHead = head ? unwrapHtmlElement(head, 'head') : '';
    const normalizedBody = unwrapHtmlElement(body, 'body');

    return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8" />
${normalizedHead}
</head>
<body>
${normalizedBody}
</body>
</html>`;
};

export const buildPreviewDocument = (body: string | null | undefined, plainText: boolean = false): string => {
    const content = plainText ? `<pre style="white-space: pre-wrap; font: inherit; margin: 0;">${escapeHtml(body ?? '')}</pre>` : (body ?? '');
    if (isFullHtmlDocument(content)) return content;

    return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
</head>
<body>
${content}
</body>
</html>`;
};
